import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { compact, uniq, flatten, isEmpty } from 'lodash';

import { operations as userOperations } from '../../state/modules/users';
import { operations as rankOperations } from '../../state/modules/ranks';
import { operations as membershipOperations } from '../../state/modules/memberships';
import { operations as skillOperations } from '../../state/modules/skills';
import { operations as groupOperations } from '../../state/modules/groups';
import { selectors as groupSelectors } from '../../state/modules/groups';
import { operations as combinedScheduleOperations } from '../../state/modules/combined-schedules';
import { selectors as userSelectors } from '../../state/modules/users';
import { selectors as membershipSelectors } from '../../state/modules/memberships';
import { augmentUser } from '../../state/modules/users/user-contact-selectors';
import moment from 'moment';

import UserPopover from './UserPopover';

const UserContact = props => {
  const [detailsLoaded, setDetailsLoaded] = useState(false);
  const [shouldFetchDetails, setShouldFetchDetails] = useState(false);

  const simpleUser = props.users[props.userId];

  useEffect(() => {
    props.fetchRanks();
  }, []);

  useEffect(() => {
    if (!simpleUser)
      props.fetchUsers({
        ids: [props.userId],
        params: { presentation_type: 'basic' },
        onlyMissing: true
      });
  }, [simpleUser]);

  // When the details of the user are loaded, we fetch the memberships, skills,
  // groups and combined schedules of the user
  useEffect(() => {
    if (!shouldFetchDetails || detailsLoaded || isEmpty(simpleUser?.memberships)) return;

    const membershipIds = compact(simpleUser.memberships.map(m => m.id));
    const skillIds = uniq(flatten(simpleUser.memberships.map(m => m.skills)));
    const groupIds = simpleUser.memberships.map(m => m.group_id);

    props.fetchMemberships({ ids: membershipIds, onlyMissing: true });
    props.fetchSkills({ ids: skillIds, onlyMissing: true });

    // We may have fetched the groups as basic groups elsewhere, so we do a force fetch
    // to ensure we get the the information we need about hierarchy for relevant groups.
    props.fetchGroups({ ids: groupIds, params: { in_hierarchy: true }, onlyMissing: true });

    props.fetchCombinedSchedules({
      membershipIds,
      type: 'membership',
      start_time: moment().startOf('day').format(),
      look_ahead: 86_400
    });

    setDetailsLoaded(true);
  }, [simpleUser?.memberships, shouldFetchDetails, detailsLoaded]);

  const fetchFullUser = useCallback(async () => {
    await props.fetchUsers({ params: { ids: [props.userId] }, forceFetch: true });
    setShouldFetchDetails(true);
  }, [props.userId, props.fetchUsers]);

  const user = useMemo(() => props.augmentUser(props.userId) || {}, [simpleUser, props.memberships]);

  // Current version displays full name, direct message link, nickname, email
  // and phone numbers spreading the props is to Popover is necessary to
  // proplery set the position, as explained in react-bootstrap documentation
  const { displayFullName, groupAvailabilityContextId } = props;

  return (
    <span className={props.className}>
      <UserPopover
        displayFullName={displayFullName}
        user={user}
        tree={props.groupTree}
        groupId={groupAvailabilityContextId}
        loadDetails={fetchFullUser}
        detailsLoaded={detailsLoaded}
      />
    </span>
  );
};

const mapStateToProps = state => ({
  users: userSelectors.byId(state),
  memberships: membershipSelectors.byId(state),
  usersLoading: state.users.loading,
  groupTree: groupSelectors.tree(state),
  augmentUser: augmentUser(state)
});

const mapDispatchToProps = {
  fetchUsers: userOperations.fetch,
  fetchRanks: rankOperations.fetch,
  fetchMemberships: membershipOperations.fetch,
  fetchSkills: skillOperations.fetch,
  fetchGroups: groupOperations.fetch,
  fetchGroupAndSubgroups: groupOperations.fetchGroupAndSubgroups,
  fetchCombinedSchedules: combinedScheduleOperations.fetchMultiple
};

export default connect(mapStateToProps, mapDispatchToProps)(UserContact);
